import React from 'react';
import { AlertTypes } from '@athena/forge';
import AmplitudeUtils from '../../utils/amplitude-utils';
import { APP_TYPES, INVALID_IDP_ERROR_BANNER_KEYS } from '../../constants';
import athenaConfig from '../../config';
import { getCustomLoginMetadata, processCreds } from '../../utils/authorize-utils';
import { getAthenaOneParamsFromJwt, getBannerConfig } from '../../utils/banner-jwt-utils';
import {
  getAthenaOneSSOLoginTimedOutUrl,
  getAthenaOneSSOLoginUrl,
  getAthenaOneSsoReauthUrl,
  isAthenaOneWebClient,
  isAthenaOneWebOrMobileClient,
  isAthenaOneWebSessionTimedOut,
  isAthenaOneWebSso
} from '../../utils/app-utils';
import { createWrapper } from '../../utils/react-utils';
import Header, { FontSize } from '../../components/common/Header';
import InfoContainer from '../../components/common/InfoContainer';
import TermsContainer from '../../components/primary-auth/TermsContainer';
import Footer from '../../components/primary-auth/Footer';
import MarketingContent from '../../components/primary-auth/MarketingContent';
import ForgotPasswordLink from '../../components/primary-auth/ForgotPasswordLink';
import ExtrasSection from '../../components/primary-auth/ExtrasSection';
import MutedText from '../../components/common/MutedText';
import { closeOktaSession, getAuthenticatorMethodType, hideWidgetForm, isPrimaryAuthController } from '../../utils/widget-facade';
import { applyMarketingFailoverLayout, applyMarketingLayout, removeMarketingLayout } from '../../utils/layout-utils';
import BaseForgifyCustomizer from '../base-forge';
import { getLocalizedString } from '../../utils/i18n-utils';
import { setUserStartLoginWorkflowCookie, clearUserStartLoginWorkflowCookie } from '../../utils/storage-utils';

const { clientIdsForBanner, marketingContentUrl } = athenaConfig;

export default class PrimaryAuthCustomizer extends BaseForgifyCustomizer {
  rememberMeSelector = '#athena-rememberMe-input-field-container';
  allowRegistration = !!settings.allowRegistration;
  headerFontSize = 'large' as FontSize;
  primaryButtonFullWidth = !isAthenaOneWebOrMobileClient();
  displayHeader = !isAthenaOneWebOrMobileClient();
  inputFieldsCustomClass = 'athena-input-form-field fe_u_padding--bottom-small';
  primaryButtonClassName = 'primary-auth-login-button';
  protected bannerConfig: any = {};

  onInfoContainerLinkClick = async () => {
    await AmplitudeUtils.logCustomEvent('BlockedAccount_ResetPasswordLinkClick', {});

    // Find and trigger click on athena forgot password button
    const $forgotPasswordButton = jQueryCourage('#athena-forgot-password-link button');

    if ($forgotPasswordButton.length > 0) {
      $forgotPasswordButton.trigger('click');
    }
    else {
      // Fallback to legacy flow if athena forgot password button element not found
      window.location.assign(athenaConfig.athenaNetPasswordResetUrl);
    }
  };

  async shouldCustomize() {
    return isPrimaryAuthController(this.context?.controller);
  }

  async preCustomize() {
    await super.preCustomize();
    if (!isAthenaOneWebOrMobileClient()) {
      this.inputFieldsCustomClass = 'athena-input-form-field fe_u_padding--bottom-large';
    }
  }

  clearInfoBanner() {
    this.eventEmitter.emitEventToComponent({
      componentName: InfoContainer.displayName as string,
      eventName: 'set-info-banner',
      eventPayload: {},
    });
  }

  primaryButtonOnClick = async () => {
    const { athenaOneWebSso, athenaOneParams } = this.controllerContext;
    if (athenaOneWebSso && athenaOneParams?.SSOREAUTHPARAMS) {
      await closeOktaSession();
      await AmplitudeUtils.logCustomEvent('SsoReauthenticationInitiated', { bannerType: athenaOneParams.BANNERTYPE });
      if (athenaOneParams.BANNERTYPE === 'LOGINTIMEDOUT') {
        window.location.href = getAthenaOneSSOLoginTimedOutUrl(athenaOneParams)!.toString();
      }
      else if (athenaOneParams.BANNERTYPE === 'LOGOUT' || athenaOneParams.BANNERTYPE === 'ERROR') {
        window.location.href = getAthenaOneSSOLoginUrl(athenaOneParams)!.toString();
      }
      else {
        window.open(getAthenaOneSsoReauthUrl(this.settings.clientId, athenaOneParams));
      }
    }
    else {
      this.clearInfoBanner();
    }
  };

  private setBanner({ headerText, description, alertType, bannerKey }:
    { headerText: string; description: string | React.ReactNode; alertType?: AlertTypes; bannerKey?: string }) {
    this.eventEmitter.emitEventToComponent({
      componentName: InfoContainer.displayName as string,
      eventName: 'set-info-banner',
      eventPayload: {
        headerText,
        description,
        alertType,
        bannerKey,
      },
    });
  }

  private addBanner(bannerDetails: any) {
    if ([ 'primary-auth', 'idp-discovery' ].includes(this.context.controller)) {
      setTimeout(() => this.setBanner(bannerDetails));
    }
  }

  private getBannerDetails() {
    return {
      headerText: getLocalizedString('portal.degradedstate.header'),
      description: getLocalizedString('portal.degradedstate.description'),
    };
  }

  private isDegradedStateBannerToBeShown() {
    return !this.settings.isAuthnWithLwaClientId && clientIdsForBanner.includes(this.settings.clientId);
  }

  private setDegradedStateBanner() {
    const { headerText, description } = this.getBannerDetails();
    this.addBanner({ headerText, description });
  }

  private addDegradedStateBanner() {
    if (!this.isDegradedStateBannerToBeShown()) return;
    this.setDegradedStateBanner();
  }

  async populateControllerContext() {
    const athenaOneParams = await getAthenaOneParamsFromJwt();
    this.controllerContext = {
      athenaOneParams,
      athenaOneWebSessionTimedOut: isAthenaOneWebSessionTimedOut(athenaOneParams),
      athenaOneWebSso: isAthenaOneWebSso(athenaOneParams),
    };
    this.suppressOktaClick = !!this.controllerContext.athenaOneWebSso;
  }

  private async displayBannerBasedOnConfig() {
    const { athenaOneParams, athenaOneWebSso } = this.controllerContext;
    const bannerConfig = await getBannerConfig(athenaOneParams, athenaOneWebSso);
    this.bannerConfig = bannerConfig;
    if (bannerConfig) {
      this.logInfoBannerAmplitudeEvents({
        ohswBannerKey: bannerConfig.bannerType,
        isSso: bannerConfig.isSso,
      });
      this.addBanner({
        headerText: bannerConfig.header,
        description: bannerConfig.description,
        alertType: bannerConfig.alertType,
        bannerKey: bannerConfig.key,
      });
    }
  }

  async customize() {
    await this.populateControllerContext();
    await super.customize();
    await this.displayBannerBasedOnConfig();
    AmplitudeUtils.logCustomEvent('PrimaryAuthLanding', getCustomLoginMetadata(this.bannerConfig));
    this.addAthenaOneWebCustomizations();
    this.addDegradedStateBanner();
    this.addTitleMessage();
    this.showTAndCText();
    this.hideCheckBox();
    this.hideFormElementsForIDPLoginError();
    [ 'primary-auth', 'mfa-verify-password' ].includes(this.context.controller) && this.onIdxProceed();
    this.addPrimaryAuthErrorBanner();
    clearUserStartLoginWorkflowCookie();
  }

  protected hideCheckBox() {
    const rememberMeContainer: JQuery<HTMLElement> = jQueryCourage(this.rememberMeSelector);
    this.hideComponent(rememberMeContainer[0]);
  }

  private addMarketingContent() {
    applyMarketingLayout();
    const marketingWrapper = createWrapper({ hidden: false });
    const marketingContainer = jQueryCourage('#marketing-container');
    marketingContainer.append(marketingWrapper);
    this.renderReactComponent({
      id: 'marketing',
      container: marketingWrapper,
      className: 'marketing-content',
      component: <MarketingContent marketingContentUrl={marketingContentUrl} />,
    });
    this.eventEmitter.on('marketing-content-failed', () => {
      removeMarketingLayout();
      applyMarketingFailoverLayout();
    });
  }

  protected addCopyrightText() {
    const authContentContainer = jQueryCourage('#okta-sign-in .auth-content');
    if (authContentContainer.length) {
      const copyrightText = createWrapper({ hidden: false });
      authContentContainer.after(copyrightText);
      const footerText = `© ${new Date().getFullYear()} athenahealth, Inc`;
      const component = (
        <div className={'fe_u_font-size--small fe_u_text-align--center'}>
          <MutedText text={footerText} />
        </div>
      );
      this.renderReactComponent({
        id: 'copyright',
        container: copyrightText,
        component,
      });
    }
  }

  private logInfoBannerAmplitudeEvents({ ohswBannerKey, isSso }: {ohswBannerKey: string; isSso?: boolean}) {
    if (ohswBannerKey === 'TIMEDOUT') {
      AmplitudeUtils.logCustomEvent('TimeoutLanding', { isSso });
    }
    else if (ohswBannerKey === 'LOGOUT') {
      AmplitudeUtils.logCustomEvent('LogoutLanding', { });
    }
    else if (ohswBannerKey === 'REFRESHTIMEOUT') {
      AmplitudeUtils.logCustomEvent('RefreshTimeoutLanding', { isSso });
    }
  }

  private addPrimaryAuthExtras() {
    const primaryAuthForm = jQueryCourage('form');
    const additionalLinksWrapper = createWrapper({ hidden: false });
    primaryAuthForm.append(additionalLinksWrapper);
    this.renderReactComponent({
      id: 'additional-links',
      container: additionalLinksWrapper,
      component: <ExtrasSection />,
    });
  }

  private addAthenaOneWebCustomizations() {
    if (!this.requestContext) return;
    if (!isAthenaOneWebClient()) return;
    if (marketingContentUrl) {
      this.addMarketingContent();
    }
    else {
      applyMarketingFailoverLayout();
    }
    this.addPrimaryAuthExtras();
    this.addForgotPasswordLink();
  }

  private addForgotPasswordLink() {
    const formContainer: JQuery<HTMLElement> = jQueryCourage('[data-se="o-form-content"]');
    const container = createWrapper({ hidden: false });
    formContainer.append(container);

    const component = <ForgotPasswordLink />;
    this.renderReactComponent({
      id: 'forgot-password-link',
      component,
      container,
    });
  }

  private onIdxProceed() {
    const authClient = this.oktaSignIn.authClient;
    const originalProceed = authClient.idx.proceed.bind(authClient);
    authClient.idx.proceed = async (...args: any) => {
      if (this.context.controller === 'primary-auth' && args[0]?.identifier) {
        await processCreds({ username: args[0].identifier });
        setUserStartLoginWorkflowCookie(args[0].identifier);
      }

      const res = await originalProceed.apply(authClient, args);

      if (args?.length) {
        const currentStep = args[0].step;
        const isVerifyPasswordView =
          currentStep === 'challenge-authenticator' && getAuthenticatorMethodType() === 'password';
        if (args[0].credentials?.passcode && (currentStep === 'identify' || isVerifyPasswordView)) {
          AmplitudeUtils.logCustomEvent('LoginInitiated', getCustomLoginMetadata(this.bannerConfig));
          if (res?.context?.messages?.value?.length) {
            const errorObject = res.context.messages.value[0];
            AmplitudeUtils.logCustomEvent('LoginError', {
              ...errorObject,
              ...getCustomLoginMetadata(this.bannerConfig),
            });
          }
          else {
            AmplitudeUtils.logCustomEvent('LoginSuccess', {
              ...getCustomLoginMetadata(this.bannerConfig),
              userId: res?.context?.user?.value?.id,
            });
          }
        }
        res?.nextStep?.name === 'redirect-idp' &&
          (await AmplitudeUtils.logIdpDiscoveryUserLoginEvent({ isIdpDiscoverySSOUser: true }));
        if (isVerifyPasswordView) {
          await AmplitudeUtils.logIdpDiscoveryUserLoginEvent({ isIdpDiscoverySSOUser: false });
        }
      }
      return res;
    };
  }

  private addPrimaryAuthErrorBanner() {
    if (
      [ 'primary-auth', 'idp-discovery' ].includes(this.context.controller) &&
      this.settings.emailTokenUserRegisteredWarning
    ) {
      this.eventEmitter.emitEventToComponent({
        componentName: InfoContainer.displayName as string,
        eventName: 'set-info-banner',
        eventPayload: {
          title: '',
          description: getLocalizedString('email.token.user.exists.warning'),
          alertType: 'attention',
        },
      });
    }

    if (
      [ 'primary-auth', 'idp-discovery' ].includes(this.context.controller) &&
      this.settings.tempPasswordHint
    ) {
      this.eventEmitter.emitEventToComponent({
        componentName: InfoContainer.displayName as string,
        eventName: 'set-info-banner',
        eventPayload: {
          title: '',
          description: getLocalizedString('temp.password.hint'),
          alertType: 'attention',
        },
      });
    }
  }

  protected getFooter(): React.JSX.Element {
    return (
      <Footer
        footerLinks={jQueryCourage('.js-cancel')}
        forgotPasswordLink={!!(!isAthenaOneWebOrMobileClient() && getAuthenticatorMethodType() === 'password' && this.settings.appType) }
      />
    );
  }

  protected addTitleMessage() {
    const titleMessage = getLocalizedString('primaryauth.titlemessage');
    if (!isAthenaOneWebOrMobileClient()) {
      const athenaHeaderContainer: JQuery<HTMLElement> = jQueryCourage('[data-se="o-form-head"]');
      const container = createWrapper({ hidden: false });
      athenaHeaderContainer.after(container);
      const component = (
        <Header
          headerText={titleMessage}
          fontSize="default"
          fontColor="default"
          fontWeight="regular"
        />
      );
      this.renderReactComponent({
        id: 'okta-form-titlemessage',
        component,
        container,
      });
    }
  }

  protected forgifyController() {
    super.forgifyController();
    if (isAthenaOneWebOrMobileClient()) {
      this.addCopyrightText();
    }
  }

  private showTAndCText() {
    const { requestContext, settings } = this;
    if (!requestContext) return;
    if (settings.appType !== APP_TYPES.ATHENANET_USER) return;
    const formContainer: JQuery<HTMLElement> = jQueryCourage('[data-se="o-form-content"]');
    const container = createWrapper({ hidden: false });
    formContainer.after(container);
    const component = <TermsContainer />;
    this.renderReactComponent({
      id: 'o-form-terms-container',
      component,
      container,
    });
  }

  protected shouldFormElementsBeHiddenForIDPLoginError() {
    const { athenaOneParams } = this.controllerContext;
    return athenaOneParams && INVALID_IDP_ERROR_BANNER_KEYS.includes(athenaOneParams.KEY);
  }

  private hideFormElementsForIDPLoginError() {
    if (this.shouldFormElementsBeHiddenForIDPLoginError()) {
      hideWidgetForm(true);
      AmplitudeUtils.logCustomEvent('InvalidIdentityProvider', { });
    }
  }
}
